import { Link } from "react-router-dom";
import type { BookingWithVehicle } from "@/types";
import { formatKES, vehicleTitle } from "@/lib/format";
import { formatLongDate } from "@/lib/date";
import { canLeaveReview, countDaysLabel, isAwaitingPayment, isCancelled } from "@/lib/bookings";
import BookingStatusBadge from "@/components/base/BookingStatusBadge";
import DamageFlagBadge from "@/components/base/DamageFlagBadge";

const FALLBACK_IMAGE =
  "https://readdy.ai/api/search-image?query=Studio%20automotive%20photography%20of%20a%20sleek%20silhouette%20car%20on%20a%20seamless%20charcoal%20grey%20background%20with%20soft%20reflective%20floor%20dramatic%20softbox%20lighting%20premium%20editorial%20advertisement%20style&width=1200&height=800&seq=gari-placeholder-01&orientation=landscape";

interface TripCardProps {
  trip: BookingWithVehicle;
  reviewed: boolean;
  onReview: (trip: BookingWithVehicle) => void;
  flagged?: boolean;
}

export default function TripCard({ trip, reviewed, onReview, flagged = false }: TripCardProps) {
  const vehicle = trip.vehicle;
  const image = vehicle?.images?.[0]?.url || FALLBACK_IMAGE;
  const title = vehicle ? vehicleTitle(vehicle.make, vehicle.model, vehicle.year) : "Your Gari car";
  const awaitingPayment = isAwaitingPayment(trip.status);
  const cancelled = isCancelled(trip.status);
  const eligibleForReview = canLeaveReview(trip);

  return (
    <article className="overflow-hidden rounded-2xl surface-card">
      <div className="flex flex-col gap-4 p-4 sm:flex-row sm:p-5">
        <Link
          to={vehicle ? `/vehicle/${vehicle.id}` : "/browse"}
          className="h-36 w-full shrink-0 overflow-hidden rounded-xl bg-background-100 sm:h-28 sm:w-44"
        >
          <img
            src={image}
            alt={`${title} booked via Gari`}
            title={`${title} — Gari trip`}
            className="h-full w-full object-cover object-top transition-transform duration-500 hover:scale-105"
          />
        </Link>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wider text-foreground-500">Booking #{trip.id}</p>
              <h3 className="mt-1 truncate font-heading text-base font-semibold text-foreground-950">{title}</h3>
              <p className="mt-1 flex items-center gap-1.5 text-sm text-foreground-600">
                <i className="ri-map-pin-line text-foreground-500"></i>
                <span className="truncate">{trip.pickup_location || vehicle?.city || "Kenya"}</span>
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <BookingStatusBadge status={trip.status} />
              {flagged && <DamageFlagBadge />}
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-600">
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-calendar-line text-primary-500"></i>
              {formatLongDate(trip.start_date)} → {formatLongDate(trip.end_date)}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-time-line text-primary-500"></i> {countDaysLabel(trip.days)}
            </span>
            {eligibleForReview && reviewed && (
              <span className="inline-flex items-center gap-1.5 text-accent-300">
                <i className="ri-checkbox-circle-fill"></i> Reviewed
              </span>
            )}
          </div>

          <div className="mt-4 flex flex-wrap items-end justify-between gap-3 border-t border-background-200/70 pt-3">
            <div>
              <p className="text-xs text-foreground-500">Total</p>
              <p className="font-heading text-lg font-semibold text-foreground-950">{formatKES(trip.total_price)}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {vehicle && (
                <Link
                  to={`/vehicle/${vehicle.id}`}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-3.5 py-2 text-sm font-medium text-foreground-900 transition-colors hover:border-primary-500/60"
                >
                  <i className="ri-roadster-line"></i> View car
                </Link>
              )}
              {awaitingPayment && !cancelled ? (
                <Link
                  to={`/checkout/${trip.id}`}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3.5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-secure-payment-line"></i> Complete payment
                </Link>
              ) : (
                <Link
                  to={`/booking/${trip.id}`}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-3.5 py-2 text-sm font-medium text-foreground-900 transition-colors hover:border-primary-500/60"
                >
                  <i className="ri-chat-3-line"></i> Pickup chat
                </Link>
              )}
              {eligibleForReview && !reviewed && (
                <button
                  type="button"
                  onClick={() => onReview(trip)}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-3.5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
                >
                  <i className="ri-star-line"></i> Leave a review
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}